import { useParams } from "react-router-dom";
import { posthog } from "posthog-js";

import {
  Building,
  PlusCircle,
  User,
  MapPin,
  X,
  Headset,
  Info,
} from "lucide-react";
import Dashboard from "@/components/pages/dashboard";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { AddOpportunityDialog } from "@/components/dialogs/company/opportunity/add-opportunity";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import Logo from "@/assets/logo_full_name.svg";
import formatBudgetRange from "@/utils/formatBudgetRange";

import {
  MaskedCandidateStatus,
  MaskedJobOpening,
  usePublicOpportunityQuery,
} from "@/queries/opportunity/public-opportunity";

import dateStringToLocalDate from "@/utils/dateStringToLocalDate";
import { useAuth0 } from "@auth0/auth0-react";

const PublicOpening = () => {
  const { openingId } = useParams();

  const { opportunity } = usePublicOpportunityQuery(openingId ?? "");

  return (
    <>
      <Dashboard.Root>
        <Dashboard.ProfileRow>
          <Dashboard.ProfileInfo
            title="Your company"
            description="Joined today"
            fallbackIcon={Building}
            // src={company?.logoLink}
            // loading={companyLoading}
          />
          <Dashboard.ProfileActions>
            <Button variant="secondary" onClick={() => undefined}>
              <Building />
              Create Company Profile
            </Button>
            <Button onClick={() => undefined}>
              <PlusCircle />
              Add An Opportunity
            </Button>
          </Dashboard.ProfileActions>
        </Dashboard.ProfileRow>
        <div className="col-span-full">
          {opportunity ? (
            <OpportunitySection opportunity={opportunity} />
          ) : (
            <p className="w-full text-sm text-muted-foreground text-center">
              No opportunity found. Create your first one!
            </p>
          )}
        </div>
      </Dashboard.Root>
      <LoginDialog opportunity={opportunity} />
    </>
  );
};

export default PublicOpening;

const OpportunitySection = ({
  opportunity,
}: {
  opportunity: MaskedJobOpening;
}) => {
  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex flex-row gap-2 items-center">
        <h2 className="text-lg font-semibold text-[#181d27]">Matches</h2>
        <span className="px-1 text-xs text-[#71948E] bg-[#F9FBFB] border border-[#CAD3D2] rounded-3xl">
          {opportunity.candidates.length}
        </span>
      </div>

      <div className="w-full flex flex-col gap-4">
        <Opportunity opportunity={opportunity} />
      </div>
    </div>
  );
};

const Opportunity = ({ opportunity }: { opportunity: MaskedJobOpening }) => {
  const formattedBudget =
    formatBudgetRange(opportunity.budgetMinCents, opportunity.budgetMaxCents) ??
    opportunity.budgetNotes;

  let budgetString: string | undefined;
  if (!formattedBudget) {
    // No-op
  } else if (opportunity.type === "Project" && opportunity.budgetType) {
    budgetString = `${formattedBudget} / ${opportunity.budgetType === "Monthly retainer" ? "mo retainer" : "project"}`;
  } else if (opportunity.type === "Fractional") {
    budgetString = `${formattedBudget} / mo`;
  } else if (opportunity.type === "Full-time") {
    budgetString = `${formattedBudget} / yr`;
  }

  return (
    <Accordion
      collapsible
      type="single"
      className="w-full"
      defaultValue={opportunity.id.toString()}
    >
      <AccordionItem
        value={opportunity.id.toString()}
        className="w-full flex flex-col border-b-0"
      >
        <AccordionTrigger className="w-full flex flex-row no-underline hover:no-underline">
          <Avatar
            className={
              "relative size-16 bg-background border-2 border-background shadow-md mr-4"
            }
          >
            <AvatarFallback>
              <Building />
            </AvatarFallback>
          </Avatar>
          <div className="w-full flex flex-col gap-1">
            <div className="w-full flex flex-col md:flex-row items-center gap-2">
              <div className="flex flex-row items-center gap-2">
                <h4 className="text-lg font-semibold">{opportunity.role}</h4>
                <span className="rounded-full bg-border/50 border text-xs text-muted-foreground px-2 py-1">
                  {`${opportunity.candidates.length ?? 0} Candidate${opportunity.candidates.length !== 1 ? "s" : ""}`}
                </span>
              </div>
              <AddOpportunityDialog
                open={false}
                setOpen={() => undefined}
                // opening={opportunityData ?? opportunity}
                trigger={
                  <Button
                    variant="link"
                    className="text-xs text-muted-foreground hover:underline"
                  >
                    View & edit details
                  </Button>
                }
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:flex lg:flex-row gap-4 items-center">
              {opportunity.scopeTime && (
                <div className="flex flex-row gap-2 items-center">
                  <p className="text-xs text-muted-foreground">Time</p>
                  <span className="text-xs px-2 py-1 rounded-full bg-border/50">
                    {opportunity.scopeTime}
                  </span>
                </div>
              )}
              {opportunity.scopeEngagementDuration && (
                <div className="flex flex-row gap-2 items-center">
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <span className="text-xs px-2 py-1 rounded-full bg-border/50">
                    {opportunity.scopeEngagementDuration}
                  </span>
                </div>
              )}
              {budgetString && (
                <div className="flex flex-row gap-2 items-center">
                  <p className="text-xs text-muted-foreground">Pay Rate</p>
                  <span className="text-xs px-2 py-1 rounded-full bg-border/50">
                    {budgetString}
                  </span>
                </div>
              )}
              <div className="flex flex-row gap-2 items-center">
                <p className="text-xs text-muted-foreground">Start Date</p>
                <span className="text-xs px-2 py-1 rounded-full bg-border/50">
                  {opportunity.desiredStartDate
                    ? dateStringToLocalDate(
                        opportunity.desiredStartDate,
                      )?.toLocaleDateString("en-US", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })
                    : "Not Specified"}
                </span>
              </div>
              <div className="flex flex-row gap-2 items-center">
                <p className="text-xs text-muted-foreground">Status</p>
                <span className="text-xs px-2 py-1 rounded-full bg-border/50">
                  {opportunity.status}
                </span>
              </div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="w-full">
          {opportunity.candidates.length ? (
            <div className="w-full flex flex-col rounded-xl border">
              {(opportunity.status === "Role Filled (won)"
                ? opportunity.candidates.filter(
                    (candidate) => candidate.status === "Accepted by Client",
                  )
                : opportunity.candidates
              )
                .slice(0, 5)
                .map((candidate) => (
                  <CompanyOpportunityCandidate
                    key={candidate.id}
                    candidate={candidate}
                  />
                ))}
            </div>
          ) : (
            <p className="w-full text-xs text-muted-foreground text-center">
              No candidates yet- check back later!
            </p>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

const CompanyOpportunityCandidate = ({
  candidate,
}: {
  candidate: MaskedCandidateStatus;
}) => {
  return (
    <div
      key={candidate.id}
      className="w-full flex flex-col gap-2 md:flex-row md:gap-0 justify-between items-center p-4 border-b last-of-type:border-b-0"
    >
      <div className="flex flex-row items-center gap-4">
        <Avatar
          className={
            "relative size-12 bg-background border-2 border-background shadow-md"
          }
        >
          <AvatarFallback>
            <User />
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col">
          <h5 className="text-lg font-semibold">Anonymous Candidate</h5>
          <div className="flex flex-row items-center gap-x-1 md:gap-x-0 flex-wrap md:flex-nowrap">
            <span className="flex flex-row items-center gap-1 md:pr-2">
              <p className="text-sm font-semibold text-purple-600">Matched</p>
            </span>
            {Boolean(candidate.location) && (
              <div className="col-span-2 flex flex-row items-center gap-2 md:border-l md:pl-2">
                <MapPin className="size-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {candidate.location}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-row items-center flex-wrap gap-2 md:gap-4">
        <Tooltip content="Reject candidate" asChild>
          <Button variant="outline" size="icon" disabled>
            <X />
          </Button>
        </Tooltip>
        <Tooltip content="Once the candidate signals interest, you will be able to view their profile">
          <Button variant="outline" size="icon" disabled>
            <Info />
          </Button>
        </Tooltip>
        <Button variant="outline" disabled>
          <Headset />
          Schedule Interview
        </Button>
      </div>
    </div>
  );
};

const LoginDialog = ({
  opportunity,
}: {
  opportunity: MaskedJobOpening | null;
}) => {
  const { loginWithRedirect } = useAuth0();

  const handleLogin = () =>
    void (async () => {
      posthog.capture("hire_in_seconds_sign_up");
      await loginWithRedirect({
        appState: {
          returnTo: "/pre-screen/company/two",
        },
      });
    })();

  const handleSignUp = () =>
    void (async () => {
      posthog.capture("hire_in_seconds_sign_up");
      await loginWithRedirect({
        appState: {
          returnTo: "/pre-screen/company/two",
        },
        authorizationParams: {
          screen_hint: "signup",
        },
      });
    })();

  if (!opportunity) {
    return;
  }

  return (
    <Dialog open>
      <DialogContent
        className="w-full !max-w-[min(calc(100vw-2rem),400px)] p-6 rounded-lg max-h-screen max-w-screen left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]"
        noClose
      >
        <DialogHeader className="items-center">
          <img
            className="w-[200px] h-[36.473px] object-contain"
            src={Logo}
            alt="Venturous logo"
          />
          <DialogTitle className="sr-only">Log in to see more</DialogTitle>
          <DialogDescription className="sr-only">
            Click &quot;Log In&quot; or &quot;Sign Up&quot; to see more
          </DialogDescription>
        </DialogHeader>
        {/* <ScrollArea className="max-h-[calc(100vh-10rem)] md:max-h-[70vh] px-6 pb-6"> */}
        <div className="flex flex-col gap-1 text-center">
          <p className="text-base font-semibold text-[#0c0d0d]">
            {`We’ve found ${opportunity.candidates.length ?? 0} candidate${opportunity.candidates.length !== 1 ? "s" : ""} for your role`}
          </p>
          <p className="text-sm font-normal text-[#787D7C]">
            To see candidate profiles, request interviews, and start the hiring
            process — create an account.
          </p>
        </div>
        <div className="flex gap-4 items-center">
          <Button
            type="button"
            className="mt-2 flex-1"
            variant="outline"
            onClick={handleSignUp}
          >
            Sign Up
          </Button>
          <Button
            variant="secondary"
            type="submit"
            className="mt-2 flex-1"
            onClick={handleLogin}
          >
            Log In
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
